import React, { useState, useEffect } from 'react';
import { Card } from '../../ui/card';
import { Button } from '../../ui/button';
import { Plus, X, Check, Loader2, Tag } from 'lucide-react';
import { api } from '../../../lib/api';
import { toast } from 'sonner';

const CandidateCategoryTags = ({ candidate, candidateId, onUpdated }) => {
  const [allCategories, setAllCategories] = useState([]);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    // Initialize from candidate.category_tags or fallback
    const initial = (candidate.category_tags || []).map(t => t.id).filter(Boolean);
    if (initial.length === 0) {
      const prim = candidate.primary_category || candidate.job_category;
      if (prim) initial.push(prim);
    }
    setSelected(initial);
  }, [candidate]);

  useEffect(() => {
    fetch(`/api/job-categories`).then(() => {});
    (async () => {
      try {
        const res = await api.get('/job-categories');
        if (res.ok) {
          const data = await res.json();
          setAllCategories((data.categories || []).filter(c => c.is_active !== false));
        }
      } catch (_) { /* ignore */ }
    })();
  }, []);

  const toggleCategory = (catId) => {
    setSelected(prev => {
      if (prev.includes(catId)) {
        return prev.filter(c => c !== catId);
      }
      if (prev.length >= 3) {
        toast.error('Maximum 3 catégories par candidat');
        return prev;
      }
      return [...prev, catId];
    });
  };

  const moveFirst = (catId) => {
    setSelected(prev => [catId, ...prev.filter(c => c !== catId)]);
  };

  const save = async () => {
    if (selected.length === 0) {
      toast.error('Sélectionnez au moins une catégorie');
      return;
    }
    setSaving(true);
    try {
      const res = await api.patch(`/admin/candidates/${candidateId}/profile`, {
        job_categories_list: selected,
      });
      if (res.ok) {
        toast.success('Catégories mises à jour');
        setEditing(false);
        if (onUpdated) onUpdated();
      } else {
        toast.error('Échec de la mise à jour');
      }
    } catch (e) {
      toast.error('Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  const tags = candidate.category_tags || [];
  const catMap = Object.fromEntries(allCategories.map(c => [c._id, c]));

  if (!editing) {
    return (
      <Card className="p-4 md:p-6 border-tp-border" data-testid="candidate-category-tags-card">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Tag className="h-4 w-4 md:h-5 md:w-5 text-tp-accent" />
            <h3 className="text-sm font-semibold text-tp-text">Catégories</h3>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setEditing(true)}
            className="text-tp-text-light hover:text-tp-primary text-xs h-7"
            data-testid="edit-categories-btn"
          >
            Modifier
          </Button>
        </div>
        {tags.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {tags.map((t, i) => (
              <span
                key={t.id || i}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md"
                style={{
                  backgroundColor: `${t.color || '#0F2744'}1A`,
                  color: t.color || '#0F2744',
                }}
              >
                {i === 0 && <span className="text-[10px] font-bold uppercase opacity-70">1er</span>}
                {t.label_fr}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-sm text-tp-text-light italic">Aucune catégorie attribuée</p>
        )}
      </Card>
    );
  }

  return (
    <Card className="p-4 md:p-6 border-tp-primary/30 bg-tp-primary/5" data-testid="candidate-category-tags-edit">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Tag className="h-4 w-4 text-tp-accent" />
          <h3 className="text-sm font-semibold text-tp-text">Modifier les catégories</h3>
        </div>
        <span className="text-xs text-tp-text-light">{selected.length}/3</span>
      </div>

      {/* Selected tags (reorderable primary) */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3 pb-3 border-b border-tp-border">
          {selected.map((cid, i) => {
            const cat = catMap[cid];
            if (!cat) return null;
            return (
              <span
                key={cid}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md"
                style={{
                  backgroundColor: `${cat.color || '#0F2744'}1A`,
                  color: cat.color || '#0F2744',
                }}
              >
                {i === 0 && <span className="text-[10px] font-bold uppercase opacity-70">1er</span>}
                {i > 0 && (
                  <button
                    type="button"
                    onClick={() => moveFirst(cid)}
                    className="text-[10px] opacity-60 hover:opacity-100"
                    title="Définir comme principal"
                  >
                    ↑
                  </button>
                )}
                {cat.label_fr}
                <button
                  type="button"
                  onClick={() => toggleCategory(cid)}
                  className="opacity-60 hover:opacity-100"
                  data-testid={`remove-tag-${cid}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}

      {/* Available categories */}
      <div className="flex flex-wrap gap-1.5 mb-3">
        {allCategories.filter(c => c._id !== 'autre' && !selected.includes(c._id)).map(cat => (
          <button
            key={cat._id}
            type="button"
            onClick={() => toggleCategory(cat._id)}
            disabled={selected.length >= 3}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md border border-gray-200 hover:border-gray-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid={`add-tag-${cat._id}`}
          >
            <Plus className="h-3 w-3" />
            {cat.label_fr}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          onClick={save}
          disabled={saving || selected.length === 0}
          className="bg-tp-accent hover:bg-tp-accent/90 text-white"
          data-testid="save-categories-btn"
        >
          {saving ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Check className="h-3 w-3 mr-1" />}
          Enregistrer
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setEditing(false)}
          disabled={saving}
        >
          Annuler
        </Button>
      </div>
    </Card>
  );
};

export default CandidateCategoryTags;
